import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { applyLang, getLang, t, type Lang } from "@/i18n/t";
import { formatDate, formatDateTime } from "@/lib/format";

interface I18nState {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: typeof t;
  /** Bound to the current language, so call sites need not pass it. */
  formatDate: (iso: string | null | undefined) => string;
  formatDateTime: (iso: string | null | undefined) => string;
}

const I18nContext = createContext<I18nState | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(getLang);

  // The stored language is applied on mount as well as on change: index.html
  // ships lang="de", so a returning English user would otherwise be announced
  // as German by a screen reader.
  useEffect(() => {
    applyLang(lang);
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    applyLang(next);
    setLangState(next);
  }, []);

  const value = useMemo<I18nState>(
    () => ({
      lang,
      setLang,
      // `t` reads the module-level language, which applyLang has already
      // updated; it is re-created per language only so consumers re-render.
      t: (key, vars) => t(key, vars),
      formatDate: (iso) => formatDate(iso, lang),
      formatDateTime: (iso) => formatDateTime(iso, lang),
    }),
    [lang, setLang],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export { I18nContext };
